import { FC, useState } from 'react';
import { Close, Save } from '@mui/icons-material';
import {
  Grid,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  FormLabel,
  MenuItem,
  Theme
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  ImageModal,
  Loader,
  ManyFilesUpload,
  Page,
  PageTitle,
  SMBSelect
} from '../../../../components';
import { IModelHomeFurnitureDetails, IModelHomePayload } from '../../../../models/possessions';
import {
  getModelHomePossessionDetail,
  getModelHomePossessionFurnitureTypes,
  updateModelHomePossessionDetail,
  updatePossessionImage
} from '../../../../fetch/possessions';
import { useQuery } from 'react-query';
import { IDropdownResponse } from '../../../../models';
import makeStyles from '@mui/styles/makeStyles';

interface IModelHomePossessionsDetailsPage {}

const ModelHomeSchema = Yup.object().shape({
  code: Yup.string(),
  description: Yup.string()
    .required('Description is required')
    .max(100, 'Description must be less than or equal to 100 characters'),
  fixedAsset: Yup.boolean(),
  subType: Yup.string(),
  approvedVendor1: Yup.string().max(100, 'Vendor must be less than or equal to  100 characters'),
  approvedVendor2: Yup.string().max(100, 'Vendor must be less than or equal to  100 characters'),
  approvedVendor3: Yup.string().max(100, 'Vendor must be less than or equal to  100 characters'),
  approvedVendor4: Yup.string().max(100, 'Vendor must be less than or equal to  100 characters'),
  widthFeet: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000'),
  widthInches: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000'),
  depthFeet: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000'),
  depthInches: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000'),
  heightFeet: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000'),
  heightInches: Yup.number()
    .typeError('This value must be a number')
    .min(0, 'Must be greater than or equal to 0')
    .max(1000000, 'Must be at most 1000000')
});

export const ModelHomePossessionsDetails: FC<IModelHomePossessionsDetailsPage> = () => {
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { possessionId }: { possessionId: string } = useParams();
  const [currentImage, setCurrentImage] = useState<string>('');

  const { isLoading: isLoadingFurnitureTypes, data: furnitureTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['furnitureTypes'], async () => await getModelHomePossessionFurnitureTypes());

  const {
    isLoading: isLoadingModelHomePossessions,
    data: modelHomePossessions,
    isRefetching: isRefetchingModelHomePossessions,
    refetch: refetchModelHomePossessions
  } = useQuery<IModelHomeFurnitureDetails, Error>(
    ['modelHomePossessions', possessionId],
    async () => await getModelHomePossessionDetail(possessionId),
    {}
  );

  const [isSavingImage, setIsSavingImage] = useState<boolean>(false);
  const handleImagesUpload = async (files: File[]): Promise<any> => {
    if (files?.length > 0) {
      try {
        setIsSavingImage(true);
        const formData = new FormData();
        formData.append('ImageFile', files[0]);

        const res = await updatePossessionImage(possessionId, formData);
        enqueueSnackbar('Model Home Possession Updated!', { variant: 'success' });
        await refetchModelHomePossessions();
        return res;
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Something went wrong.', { variant: 'error' });
      } finally {
        setIsSavingImage(false);
      }
    }
  };
  return (
    <>
      {!(
        isSavingImage &&
        isLoadingFurnitureTypes &&
        isLoadingModelHomePossessions &&
        isRefetchingModelHomePossessions
      ) && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            code: modelHomePossessions?.code ?? '',
            description: modelHomePossessions?.description ?? '',
            fixedAsset: modelHomePossessions?.fixedAsset ?? false,
            selectedSubType: modelHomePossessions?.selectedSubType ?? '',
            approvedVendor1: modelHomePossessions?.approvedVendors?.[0] ?? '',
            approvedVendor2: modelHomePossessions?.approvedVendors?.[1] ?? '',
            approvedVendor3: modelHomePossessions?.approvedVendors?.[2] ?? '',
            approvedVendor4: modelHomePossessions?.approvedVendors?.[3] ?? '',
            widthFeet: modelHomePossessions?.width?.feet ?? '',
            widthInches: modelHomePossessions?.width?.inches ?? '',
            depthFeet: modelHomePossessions?.depth?.feet ?? '',
            depthInches: modelHomePossessions?.depth?.inches ?? '',
            heightFeet: modelHomePossessions?.height?.feet ?? '',
            heightInches: modelHomePossessions?.height?.inches ?? ''
          }}
          validationSchema={ModelHomeSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IModelHomePayload = {
                ...values,
                description: values.description ?? '',
                fixedAsset: values.fixedAsset ?? false,
                selectedSubType: values.selectedSubType ?? '',
                approvedVendors: [
                  values.approvedVendor1,
                  values.approvedVendor2,
                  values.approvedVendor3,
                  values.approvedVendor4
                ],
                depth: { feet: values.depthFeet ?? '', inches: values.depthInches ?? '' },
                width: { feet: values.widthFeet ?? '', inches: values.widthInches ?? '' },
                height: { feet: values.heightFeet ?? '', inches: values.heightInches ?? '' }
              };
              await updateModelHomePossessionDetail(possessionId, data);
              await refetchModelHomePossessions();

              enqueueSnackbar('Model Home Possession was updated!', {
                variant: 'success'
              });
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            return (
              <Page title="Model Home Possessions">
                <PageTitle title={'Model Home Possessions'} />
                {(isSubmitting ||
                  isSavingImage ||
                  isLoadingFurnitureTypes ||
                  isLoadingModelHomePossessions ||
                  isRefetchingModelHomePossessions) && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Code"
                              name="code"
                              size="small"
                              value={values.code}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Description"
                              name="description"
                              size="small"
                              value={values.description}
                              onBlur={handleBlur}
                              error={touched.description && !!errors.description}
                              helperText={touched.description && errors.description}
                              onChange={e => setFieldValue('description', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field name={`fixedAsset`}>
                              {({ field }) => {
                                return (
                                  <FormControl component="fieldset">
                                    <FormLabel>Fixed Asset</FormLabel>
                                    <RadioGroup
                                      sx={{
                                        position: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'left',
                                        alignItems: 'start'
                                      }}
                                      {...field}
                                      aria-label="fixedAsset"
                                      name="fixedAsset"
                                    >
                                      <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label="Yes"
                                      />
                                      <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label="No"
                                      />
                                    </RadioGroup>
                                  </FormControl>
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              onChange={e => {
                                setFieldValue('selectedSubType', e.target.value);
                              }}
                              variant="outlined"
                              handleClear={() => setFieldValue('selectedSubType', '')}
                              name={`furnitureType`}
                              label={`Sub Type`}
                              id={`furnitureType`}
                              value={values.selectedSubType}
                              onBlur={handleBlur}
                              disabled={isLoadingFurnitureTypes}
                              error={errors && errors.selectedSubType ? true : false}
                            >
                              {furnitureTypes?.map((type: any, index) => {
                                return (
                                  <MenuItem key={`${index + 1}`} value={type.value}>
                                    {type.description}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h6">Approved Vendors</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Vendor 1"
                              name="approvedVendor1"
                              size="small"
                              error={touched.approvedVendor1 && !!errors.approvedVendor1}
                              helperText={touched.approvedVendor1 && errors.approvedVendor1}
                              value={values.approvedVendor1}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('approvedVendor1', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Vendor 2"
                              name="approvedVendor2"
                              size="small"
                              error={touched.approvedVendor2 && !!errors.approvedVendor2}
                              helperText={touched.approvedVendor2 && errors.approvedVendor2}
                              value={values.approvedVendor2}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('approvedVendor2', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Vendor 3"
                              name="approvedVendor3"
                              size="small"
                              error={touched.approvedVendor3 && !!errors.approvedVendor3}
                              helperText={touched.approvedVendor3 && errors.approvedVendor3}
                              value={values.approvedVendor3}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('approvedVendor3', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Vendor 4"
                              name="approvedVendor4"
                              size="small"
                              error={touched.approvedVendor4 && !!errors.approvedVendor4}
                              helperText={touched.approvedVendor4 && errors.approvedVendor4}
                              value={values.approvedVendor4}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('approvedVendor4', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="h6">Specs</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Width (Feet)"
                              name="widthFeet"
                              size="small"
                              value={values.widthFeet}
                              onBlur={handleBlur}
                              error={touched.widthFeet && !!errors.widthFeet}
                              helperText={touched.widthFeet && errors.widthFeet}
                              onChange={e => {
                                setFieldValue('widthFeet', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Width (Inches)"
                              name="widthInches"
                              size="small"
                              value={values.widthInches}
                              onBlur={handleBlur}
                              error={touched.widthInches && !!errors.widthInches}
                              helperText={touched.widthInches && errors.widthInches}
                              onChange={e => {
                                setFieldValue('widthInches', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Depth (Feet)"
                              name="depthFeet"
                              size="small"
                              value={values.depthFeet}
                              onBlur={handleBlur}
                              error={touched.depthFeet && !!errors.depthFeet}
                              helperText={touched.depthFeet && errors.depthFeet}
                              onChange={e => {
                                setFieldValue('depthFeet', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Depth (Inches)"
                              name="depthInches"
                              size="small"
                              value={values.depthInches}
                              onBlur={handleBlur}
                              error={touched.depthInches && !!errors.depthInches}
                              helperText={touched.depthInches && errors.depthInches}
                              onChange={e => {
                                setFieldValue('depthInches', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Height (Feet)"
                              name="heightFeet"
                              size="small"
                              value={values.heightFeet}
                              onBlur={handleBlur}
                              error={touched.heightFeet && !!errors.heightFeet}
                              helperText={touched.heightFeet && errors.heightFeet}
                              onChange={e => {
                                setFieldValue('heightFeet', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Height (Inches)"
                              name="heightInches"
                              size="small"
                              value={values.heightInches}
                              onBlur={handleBlur}
                              error={touched.heightInches && !!errors.heightInches}
                              helperText={touched.heightInches && errors.heightInches}
                              onChange={e => {
                                setFieldValue('heightInches', e.target.value);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            {modelHomePossessions?.imageSrc && (
                              <picture
                                className={classes.pictureImages}
                                onClick={() => {
                                  setCurrentImage(modelHomePossessions?.imageSrc);
                                }}
                              >
                                <img
                                  className={classes.colorImage}
                                  src={modelHomePossessions?.imageSrc}
                                  alt={`${modelHomePossessions?.description}`}
                                />
                              </picture>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <ManyFilesUpload
                              single
                              isDisabled={false}
                              imageKey={`upload-images`}
                              uploadText={modelHomePossessions?.imageSrc ? 'Change' : 'Upload'}
                              accept={'image/*'}
                              handleUploadClick={(e: any) => {
                                if (dirty && isValid) {
                                  const result = window.confirm(
                                    'You will loose any unsaved changes to this form. Are you sure you want to upload an image before saving?'
                                  );
                                  if (!result) {
                                    e.preventDefault();
                                  }
                                }
                              }}
                              handleFileChange={async (files: File[]) => {
                                await handleImagesUpload(files);
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/possessions/manage-model-home-furniture');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
                <ImageModal
                  isOpen={!!currentImage}
                  onClose={() => setCurrentImage('')}
                  source={currentImage}
                />
              </Page>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pictureImages: {
    marginRight: theme.spacing(1),
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      width: 50,
      height: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  }
}));
